import { ChevronsUpDown, Dumbbell, Search, X } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ExerciseLibraryLeaf, LibraryGroup } from './LibraryPrimitives'

const ExerciseLibraryRail = ({
  libraryPanelRef,
  exerciseLibraryFilter,
  setExerciseLibraryFilter,
  exerciseLibraryTree,
  exerciseLibraryExpanded,
  setExerciseLibraryExpanded,
  filteredExerciseLibraryTree,
  countLeaves,
  buildLibraryExpansionState,
}) => {
  const exerciseLibraryAllExpanded =
    Object.values(exerciseLibraryExpanded).every(Boolean) && Object.keys(exerciseLibraryExpanded).length > 0
  const exerciseLibraryVisibleCount = (filteredExerciseLibraryTree || []).reduce(
    (sum, node) => sum + countLeaves(node),
    0
  )

  return (
    <div ref={libraryPanelRef} className="flex min-h-0 flex-1 flex-col w-full">
      <Card className="flex flex-col flex-1 min-h-0 overflow-hidden rounded-xl border-2 border-slate-300 shadow-sm">
        <CardHeader className="flex select-none flex-row items-center justify-between gap-2 border-b-2 border-slate-300 bg-slate-50 px-3 py-2.5 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-fitness-primary/25 bg-fitness-primary/10 text-fitness-primary">
              <Dumbbell className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-sm font-semibold tracking-tight text-slate-900">Exercise Library</CardTitle>
              <p className="text-[11px] leading-tight text-slate-400">{exerciseLibraryVisibleCount} exercises · drag to a workout</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 flex-col gap-2 px-3 py-2.5">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={exerciseLibraryFilter}
                onChange={(e) => setExerciseLibraryFilter(e.target.value)}
                placeholder="Search exercises"
                className="h-9 w-full rounded-lg border-2 border-slate-300 bg-white pl-8 pr-7 text-sm text-slate-800 transition focus:border-fitness-primary focus:outline-none focus:ring-2 focus:ring-fitness-primary/20"
              />
              {exerciseLibraryFilter ? (
                <button
                  type="button"
                  onClick={() => setExerciseLibraryFilter('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                  aria-label="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() =>
                setExerciseLibraryExpanded(buildLibraryExpansionState(exerciseLibraryTree, !exerciseLibraryAllExpanded))
              }
              className="flex h-9 flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border-2 border-slate-300 bg-white px-2.5 text-xs font-semibold text-slate-600 transition hover:border-slate-400 hover:bg-slate-50"
            >
              <ChevronsUpDown className="h-3.5 w-3.5" />
              {exerciseLibraryAllExpanded ? 'Collapse all' : 'Expand all'}
            </button>
          </div>
          <div className="flex-1 space-y-1.5 overflow-auto pr-1">
            {(filteredExerciseLibraryTree || []).map((node) => {
              const expanded = exerciseLibraryExpanded[node.id]
              const toggle = () => setExerciseLibraryExpanded((prev) => ({ ...prev, [node.id]: !expanded }))
              const renderNode = (child, depth) => {
                if (!child.children?.length) return <ExerciseLibraryLeaf key={child.id} node={child} />
                const childExpanded = exerciseLibraryExpanded[child.id]
                const childToggle = () => setExerciseLibraryExpanded((prev) => ({ ...prev, [child.id]: !childExpanded }))
                return (
                  <LibraryGroup
                    key={child.id}
                    node={child}
                    expanded={childExpanded ?? false}
                    onToggle={childToggle}
                    childrenCount={countLeaves(child)}
                    depth={depth}
                  >
                    {(child.children || []).map((grand) => renderNode(grand, depth + 1))}
                  </LibraryGroup>
                )
              }
              return (
                <LibraryGroup
                  key={node.id}
                  node={node}
                  expanded={expanded ?? false}
                  onToggle={toggle}
                  childrenCount={countLeaves(node)}
                  depth={0}
                >
                  {(node.children || []).map((child) => renderNode(child, 1))}
                </LibraryGroup>
              )
            })}
            {!filteredExerciseLibraryTree?.length && (
              <div className="flex flex-col items-center gap-2 rounded-lg border-2 border-dashed border-slate-300 py-8 text-center">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-300">
                  <Search className="h-4 w-4" />
                </div>
                <p className="text-xs font-medium text-slate-500">No exercises match your search.</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default ExerciseLibraryRail
